/* eslint-disable react/no-array-index-key */
import PropTypes from 'prop-types';
import moment from 'moment';
import * as React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useColorScheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  ChevronRight,
  Close,
  DeleteOutline,
  EventOutlined,
  OpenInNew,
  TaskAlt,
} from '@mui/icons-material';
import {
  alpha,
  useTheme,
} from '@mui/material/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import {
  saveHistoricalDates,
  validateHistoricalDates,
} from './historicalRecords';
import { getChipForStatus } from '../../statusChip';
import { getQuestriPrimaryButtonSx } from '../../../../../../../../utils/questriButtonStyles';
import { red } from '../../../../../../../../utils/themePrimitives';
import Horse from '../../../../../../../../components/Horse';
import TaskHistory from '../../../TaskHistory';
import { generateEventsFromSchedules } from '../../../../../../../../utils/scheduleHelper';
import { addSchedule } from '../../../../../../../../redux/schedule/actions';
import { setGlobalModals } from '../../../../../../../../redux/app/actions';
import {
  SCHEDULE_MODAL_TYPE_DIALOG,
  TASK_MODAL_TYPE_DIALOG,
} from '../../../../../../../../redux/app/types';
import { getTagReminder } from '../../../../../../../../redux/tagReminder/actions';
import TagReminderService from '../../../../../../../../services/TagReminderService';
import {
  fromPickerValue,
  toMomentValue,
} from '../../../../../../../../utils/dateHelper';

const TagReminderModal = ({
  open,
  onClose,
  tagReminder: incomingTagReminder,
  zIndex,
  triggerDataRefresh,
  readOnly,
  onReminderRefresh,
  onReminderUnavailable,
  onEditorRefresh,
}) => {
  const dispatch = useDispatch();
  const allBarnHorses = useSelector((state) => state.barn.allBarnHorses);
  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const locationsMap = useSelector((state) => state.location.locationsMap);
  const reminderEditorOpen = useSelector((state) => !!(state.app?.openModalType && state.app?.modalConfig?.reminderDialog));
  const contextKey = JSON.stringify([incomingTagReminder.id, selectedBarn?.id, selectedBarn?.tenant, readOnly]);
  const instanceId = React.useId();
  const theme = useTheme();
  const {
    mode,
    systemMode,
  } = useColorScheme();
  const darkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');
  const fullscreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = React.useState(false)
  const [addHistory, setAddHistory] = React.useState(false)
  const [hideModal, setHideModal] = React.useState(false)
  const [error, setError] = React.useState('');
  const [historyVersion, setHistoryVersion] = React.useState(0);
  const [historicalDates, setHistoricalDates] = React.useState([moment().add(-1, 'day').startOf('day').toDate()]);
  const [refreshedReminder, setRefreshedReminder] = React.useState(null);
  const [previewError, setPreviewError] = React.useState('');
  const reminderRequest = React.useRef(0);
  const mounted = React.useRef(false);
  const currentContext = React.useRef(contextKey);
  const dialogSession = React.useRef(0);
  const historySaving = React.useRef(false);
  const editorPending = React.useRef(false);
  const editorObserved = React.useRef(false);
  const editorRefreshed = React.useRef(false);
  const editorOpenRef = React.useRef(reminderEditorOpen);
  const returnFocus = React.useRef(null);
  const closeButton = React.useRef(null);
  const wasHidden = React.useRef(false);
  const refreshCallback = React.useRef(onReminderRefresh);
  const unavailableCallback = React.useRef(onReminderUnavailable);
  refreshCallback.current = onReminderRefresh;
  unavailableCallback.current = onReminderUnavailable;
  editorOpenRef.current = reminderEditorOpen;
  currentContext.current = contextKey;
  const tagReminder = refreshedReminder?.contextKey === contextKey
    ? refreshedReminder.reminder
    : incomingTagReminder;

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      reminderRequest.current += 1;
      dialogSession.current += 1;
      historySaving.current = false;
    };
  }, []);

  React.useEffect(() => {
    reminderRequest.current += 1;
    dialogSession.current += 1;
    historySaving.current = false;
    editorPending.current = false;
    editorObserved.current = false;
    editorRefreshed.current = false;
    setHistoricalDates([moment().add(-1, 'day').startOf('day').toDate()])
    setLoading(false);
    setAddHistory(false)
    setHideModal(false);
    setError('');
    setPreviewError('');
    setRefreshedReminder(null);
  }, [contextKey, open]);

  const loadPreview = React.useCallback(async () => {
    if (!readOnly || !open || !mounted.current || currentContext.current !== contextKey) return;
    reminderRequest.current += 1;
    const requestId = reminderRequest.current;
    const session = dialogSession.current;
    const isCurrentRequest = () => mounted.current && reminderRequest.current === requestId
      && currentContext.current === contextKey && dialogSession.current === session;
    setLoading(true);
    setPreviewError('');
    try {
      const updatedReminder = await TagReminderService.getTagReminder(incomingTagReminder.id);
      if (!isCurrentRequest()) return;
      const definition = updatedReminder?.eventTemplateReminder;
      if (Number(updatedReminder?.id) !== Number(incomingTagReminder.id)
        || Number(updatedReminder?.tag?.id) !== Number(incomingTagReminder.tag.id)
        || Number(definition?.eventTemplate?.id) !== Number(incomingTagReminder.eventTemplateReminder.eventTemplate.id)) {
        throw new Error('Unexpected reminder response');
      }
      if (updatedReminder.archived || updatedReminder.tag.archived || definition.archived || definition.eventTemplate.archived) {
        setPreviewError('This horse reminder is no longer available.');
        refreshCallback.current(updatedReminder);
        return;
      }
      setRefreshedReminder({
        contextKey,
        reminder: updatedReminder,
      });
      refreshCallback.current(updatedReminder);
    } catch (refreshError) {
      if (!isCurrentRequest()) return;
      const status = refreshError?.response?.status || refreshError?.status;
      const message = status === 404 || status === 410
        ? 'This horse reminder is no longer available.'
        : 'Unable to load the current horse reminder. Try again.';
      setPreviewError(message);
      if (status === 404 || status === 410) unavailableCallback.current(incomingTagReminder.id);
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  }, [contextKey, incomingTagReminder.id, incomingTagReminder.tag.id, incomingTagReminder.eventTemplateReminder.eventTemplate.id, open, readOnly]);

  React.useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const barnHorse = (allBarnHorses || []).find((horse) => Number(horse.pingumTagId) === Number(tagReminder.tag.id));

  let nextEvents = null;
  let schedule = null;
  const upcomingSchedule = tagReminder.nextSchedule;
  const occurrenceDate = moment(upcomingSchedule?.nextOccurrenceDate, moment.ISO_8601, true);
  const scheduleEnd = moment(upcomingSchedule?.endDate, moment.ISO_8601, true);
  const withinSchedule = !upcomingSchedule?.endDate || (scheduleEnd.isValid() && occurrenceDate.isSameOrBefore(scheduleEnd));
  if (upcomingSchedule && !upcomingSchedule.archived && upcomingSchedule.nextOccurrenceDate
    && occurrenceDate.isValid() && occurrenceDate.isAfter(moment()) && withinSchedule) {
    schedule = upcomingSchedule;

    nextEvents = generateEventsFromSchedules([schedule], [], new Date(), moment(schedule.nextOccurrenceDate).add(1, 'days').toDate(), barnUsersMap, selectedBarn.barnMetadata);
  }

  const reloadTagReminder = async () => {
    if (!mounted.current || currentContext.current !== contextKey) return;
    reminderRequest.current += 1;
    const requestId = reminderRequest.current;
    const tagReminderId = tagReminder.id;
    const isCurrentRequest = () => mounted.current && reminderRequest.current === requestId
      && currentContext.current === contextKey;
    try {
      const updatedReminder = await dispatch(getTagReminder(tagReminderId));
      if (!isCurrentRequest()) return;
      if (!updatedReminder || updatedReminder.id !== tagReminderId) throw new Error('Unexpected reminder response');
      // A list request started before the save may arrive later with stale parent props.
      setRefreshedReminder({
        contextKey,
        reminder: updatedReminder,
      });
      setHistoryVersion((version) => version + 1);
      setError('');
    } catch (refreshError) {
      if (isCurrentRequest()) setError('The change was saved, but the reminder could not be refreshed. Close and retry loading reminders.');
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  };

  const handleClose = () => {
    if (!hideModal && !historySaving.current) {
      dialogSession.current += 1;
      reminderRequest.current += 1;
      if (!readOnly) triggerDataRefresh();
      onClose();
    }
  };

  React.useEffect(() => {
    if (!hideModal) return;
    if (reminderEditorOpen) {
      editorObserved.current = true;
    } else if (editorObserved.current) {
      editorPending.current = false;
      setHideModal(false);
      if (!editorRefreshed.current) reloadTagReminder();
    }
  }, [hideModal, reminderEditorOpen]);

  React.useEffect(() => {
    const focusContext = returnFocus.current;
    if (wasHidden.current && !hideModal && open && focusContext?.contextKey === contextKey
      && focusContext.session === dialogSession.current) {
      const target = focusContext.element;
      if (target?.isConnected && !target.disabled) target.focus({ preventScroll: true });
      else closeButton.current?.focus({ preventScroll: true });
    }
    wasHidden.current = hideModal;
  }, [hideModal, open, contextKey]);

  const beginEditor = () => {
    const session = dialogSession.current;
    editorPending.current = true;
    editorObserved.current = false;
    editorRefreshed.current = false;
    returnFocus.current = {
      element: document.activeElement,
      contextKey,
      session,
    };
    setLoading(true);
    setHideModal(true);
    return (reload) => {
      if (!mounted.current || currentContext.current !== contextKey || dialogSession.current !== session) return;
      onEditorRefresh(reload === true);
      editorRefreshed.current = true;
      reloadTagReminder();
      if (!editorOpenRef.current) {
        editorPending.current = false;
        setHideModal(false);
      }
    };
  };

  const handleTaskSelect = (task) => {
    if (readOnly || loading || editorPending.current || historySaving.current || !task?.id) return;
    dispatch(setGlobalModals(TASK_MODAL_TYPE_DIALOG, { id: task.id }, null, null, { reminderDialog: true }, beginEditor()));
  }

  const handleClickSchedule = (event, tr) => {
    if (readOnly || loading || editorPending.current || historySaving.current) return;
    const scheduleTags = (event && event.resource && event.resource.tags) || [];
    const locationTag = scheduleTags.find((tag) => tag.tagType && tag.tagType.id === selectedBarn.barnMetadata.locationTagTypeId);
    const onEditorChange = beginEditor();
    dispatch(setGlobalModals(SCHEDULE_MODAL_TYPE_DIALOG, null, (event && event.resource) || null, event || null, {
      reminderDialog: true,
      locationId: (locationTag && locationsMap[locationTag.id])
        ? locationsMap[locationTag.id][0].id
        : null,
      taskTagIds: event
        ? []
        : [{
          id: tr.tag.id,
          tagType: { id: selectedBarn.barnMetadata.horseTagTypeId },
        }],
      eventTemplateTagTypeFilter: tr.eventTemplateReminder.eventTemplate.id,
      openInSeries: false,
      startDate: (tr && tr.dueDate && new Date(tr.dueDate).getTime() > new Date().getTime())
        ? new Date(tr.dueDate)
        : null,
    }, onEditorChange));
  }

  const handleAddHistoricalRecords = async () => {
    if (readOnly || historySaving.current) return;
    const validationError = validateHistoricalDates(historicalDates);
    if (validationError) {
      setError(validationError);
      return;
    }
    historySaving.current = true;
    const session = dialogSession.current;
    const isCurrentSave = () => mounted.current && currentContext.current === contextKey && dialogSession.current === session;
    setLoading(true);
    setError('');

    const request = {
      duration: 0,
      name: tagReminder.eventTemplateReminder.eventTemplate.name,
      workflows: [{
        eventTemplateId: tagReminder.eventTemplateReminder.eventTemplate.id,
        tagIds: [tagReminder.tag.id],
        createEvents: true,
        startingAssignment: '',
        startingStatusId: selectedBarn.barnMetadata.statusEndId,
        startingStatusComment: 'Adding historical record for reminder tracking',
        dueDateStatusChangeId: null,
        dueDateTime: null,
        dueDateTimeEndOfDay: false,
        eventDataDefaultValues: tagReminder.eventTemplateReminder.eventTemplate.eventDataDefaultValues || {},
      }],
      overrideCancellations: false, // TODO implement/change this
      type: 'Default',
      completeTasksInPast: true,
      createTasksInPast: true,
    };

    const result = await saveHistoricalDates(historicalDates, (date) => dispatch(addSchedule(
      tagReminder.eventTemplateReminder.eventTemplate.id,
      {
        ...request,
        startDate: date,
        endDate: date,
        rrule: 'FREQ=DAILY;INTERVAL=1',
      },
    )));
    if (!isCurrentSave()) return;
    if (result.savedCount > 0) {
      await reloadTagReminder();
    }
    if (!isCurrentSave()) return;
    historySaving.current = false;
    setLoading(false);
    if (result.failedDates.length) {
      setHistoricalDates(result.failedDates);
      setError(`${result.savedCount} records saved. ${result.failedDates.length} could not be saved. Retry the remaining dates.`);
      return;
    }
    setAddHistory(false);
    setHistoricalDates([moment().subtract(1, 'day').startOf('day').toDate()]);
  };

  const validationError = validateHistoricalDates(historicalDates);
  const template = tagReminder.eventTemplateReminder.eventTemplate;
  const taskName = template.name || 'Care';
  const horseName = barnHorse?.horse?.name || tagReminder.tag.name || 'this horse';
  const title = `${taskName} for ${horseName}`;
  const titleId = `horse-reminder-title-${tagReminder.id}-${instanceId}`;
  const careTitleId = `horse-reminder-care-${tagReminder.id}-${instanceId}`;
  const historyTitleId = `horse-reminder-history-${tagReminder.id}-${instanceId}`;
  const upcomingTitleId = `horse-reminder-upcoming-${tagReminder.id}-${instanceId}`;
  const taskColor = template.eventDataDefaultValues?.calendarDisplayColor || theme.palette.primary.main;
  const iconBackgroundOpacity = darkMode
    ? 0.16
    : 0.08;
  const nextDate = schedule?.nextOccurrenceDate;
  let nextEvent = null;
  if (nextDate) {
    nextEvent = nextEvents?.[0] || {
      start: new Date(nextDate),
      resource: schedule,
    };
  }
  const { lastEvent } = tagReminder;
  const unfinishedTask = lastEvent?.id && !lastEvent.completeDate && !lastEvent.archived
    ? lastEvent
    : null;
  const needsCompletion = !!unfinishedTask || tagReminder.status === 'Incomplete';
  const hasDueDate = !!tagReminder.dueDate && moment(tagReminder.dueDate).isValid();
  const dueHeading = hasDueDate
    ? `Due ${moment(tagReminder.dueDate).format('LL')}`
    : 'No due date yet';
  const pastDueColor = darkMode
    ? red[200]
    : 'error.main';
  const dueColor = tagReminder.status === 'PastDue'
    ? pastDueColor
    : 'text.primary';
  let scheduleDescription = 'No task scheduled.';
  if (nextDate) scheduleDescription = `Next task scheduled for ${moment(nextDate).format('LL')}.`;
  if (needsCompletion) scheduleDescription = 'A task is unfinished. Open it to review or complete it.';
  if (readOnly && needsCompletion) scheduleDescription = 'A task is unfinished.';
  if (needsCompletion && !unfinishedTask) scheduleDescription = 'The unfinished task details are unavailable. Close and refresh the reminder.';
  let guidance = '';
  if (!hasDueDate && !needsCompletion) guidance = 'Add a past completion or schedule the next task to start tracking.';
  if (readOnly && !hasDueDate && !needsCompletion) guidance = 'No completed or scheduled tasks have started this reminder’s tracking.';
  if (tagReminder.status === 'ScheduleMiss') guidance = 'The scheduled task is beyond this reminder’s recommended window.';
  let actionLabel = 'Schedule Task';
  let actionIcon = <EventOutlined />;
  let primaryAction = () => handleClickSchedule(null, tagReminder);
  if (nextEvent) {
    actionLabel = 'View scheduled task';
    actionIcon = <OpenInNew />;
    primaryAction = () => handleClickSchedule(nextEvent, tagReminder);
  }
  if (needsCompletion) {
    actionLabel = 'View unfinished task';
    actionIcon = <OpenInNew />;
    primaryAction = () => handleTaskSelect(unfinishedTask);
  }
  const primaryDisabled = loading || hideModal || (needsCompletion && !unfinishedTask);
  const openHistory = () => {
    if (!readOnly && !loading && !historySaving.current) {
      setAddHistory(true);
      setError('');
    }
  };
  const previewReady = !readOnly || refreshedReminder?.contextKey === contextKey;
  const UpcomingRow = readOnly
    ? ListItem
    : ListItemButton;
  const upcomingRowProps = readOnly
    ? { component: 'div' }
    : {
      'aria-label': `Open scheduled task: ${moment(nextDate).format('LL')}`,
      onClick: () => handleClickSchedule(nextEvent, tagReminder),
      disabled: loading,
    };

  return (
    <Dialog
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          height: fullscreen
            ? '100%'
            : 'min(760px, calc(100dvh - 64px))',
          visibility: hideModal
            ? 'hidden'
            : 'visible',
        },
      }}
      sx={{ zIndex }}
      open={open}
      disableEnforceFocus={hideModal}
      disableEscapeKeyDown={hideModal}
      fullScreen={fullscreen}
      onClose={handleClose}
    >
      <DialogTitle
        component="div"
        id={`${titleId}-header`}
        sx={{
          p: 2.5,
          position: 'relative',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          {barnHorse && <Horse size={44} horse={barnHorse.horse} hideName />}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography id={titleId} variant="h6" component="h2" sx={{ overflowWrap: 'anywhere' }}>{title}</Typography>
            {barnHorse?.horse?.showName && <Typography variant="body2" color="text.secondary">{barnHorse.horse.showName}</Typography>}
          </Box>
          <IconButton ref={closeButton} aria-label="Close reminder" onClick={handleClose} disabled={hideModal || historySaving.current}>
            <Close />
          </IconButton>
        </Stack>
        {loading && !hideModal && (
          <LinearProgress
            aria-label={historySaving.current
              ? 'Saving completed tasks'
              : 'Refreshing reminder'}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 2,
            }}
          />
        )}
      </DialogTitle>
      <DialogContent
        aria-busy={loading}
        sx={{
          px: 2.5,
          pb: 2.5,
        }}
      >
        {readOnly && previewError && (
          <Alert severity="error" action={<Button onClick={loadPreview} disabled={loading}>Retry</Button>}>{previewError}</Alert>
        )}
        {readOnly && !previewReady && !previewError && <Typography color="text.secondary">Loading reminder details…</Typography>}
        {previewReady && (
          <Stack spacing={2.5}>
            <Paper
              component="section"
              aria-labelledby={careTitleId}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    flex: '1 1 160px',
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      color: taskColor,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 2,
                        backgroundColor: taskColor,
                        opacity: iconBackgroundOpacity,
                      },
                    }}
                  >
                    <TaskAlt />
                  </Box>
                  <Typography
                    id={careTitleId}
                    variant="subtitle1"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {taskName}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    flexShrink: 0,
                    ml: 'auto',
                  }}
                >
                  {getChipForStatus(tagReminder)}
                </Box>
              </Stack>
              <Typography variant="h5" component="p" color={dueColor}>{dueHeading}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Every {tagReminder.eventTemplateReminder.reminderDays} days after the last completed task
              </Typography>
              <Typography variant="body2" sx={{ mt: 1.5 }}>{scheduleDescription}</Typography>
              {guidance && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{guidance}</Typography>}
              {!readOnly && (
                <Stack
                  direction={{
                    xs: 'column',
                    sm: 'row',
                  }}
                  spacing={1}
                  sx={{ mt: 2 }}
                >
                  <Button
                    variant="contained"
                    startIcon={actionIcon}
                    sx={getQuestriPrimaryButtonSx()}
                    onClick={primaryAction}
                    disabled={primaryDisabled}
                  >{actionLabel}
                  </Button>
                </Stack>
              )}
            </Paper>
            {error && <Alert severity="error">{error}</Alert>}
            {!readOnly && addHistory && (
              <Box component="section" aria-label="Record completed task">
                <Typography variant="subtitle1" component="h3">Record completed task</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    mb: 1.5,
                  }}
                >
                  Choose completion dates before today. These records update this horse’s care history and next reminder.
                </Typography>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Stack spacing={1.5}>
                    {historicalDates.map((date, index) => {
                      const label = index === 0
                        ? 'Completion date'
                        : `Completion date ${index + 1}`;
                      return (
                        <Stack key={`completion-${index}`} direction="row" spacing={1} alignItems="center">
                          <DatePicker
                            disabled={loading}
                            label={label}
                            value={toMomentValue(date)}
                            minDate={moment().subtract(10, 'years').startOf('day')}
                            maxDate={moment().subtract(1, 'day').startOf('day')}
                            onChange={(newValue) => {
                              const dates = [...historicalDates];
                              dates[index] = fromPickerValue(newValue);
                              setHistoricalDates(dates);
                            }}
                            slotProps={{
                              popper: { sx: { zIndex: zIndex + 100 } },
                              textField: {
                                fullWidth: true,
                                required: true,
                                size: 'small',
                              },
                            }}
                          />
                          <IconButton
                            aria-label={`Remove completion date ${index + 1}`}
                            disabled={loading || historicalDates.length === 1}
                            onClick={() => setHistoricalDates(historicalDates.filter((value, dateIndex) => dateIndex !== index))}
                          ><DeleteOutline />
                          </IconButton>
                        </Stack>
                      );
                    })}
                  </Stack>
                </LocalizationProvider>
                {validationError && <Typography variant="caption" color="error">{validationError}</Typography>}
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                  <Button startIcon={<Add />} onClick={() => setHistoricalDates([...historicalDates, null])} disabled={loading}>
                    Add another date
                  </Button>
                  <Box sx={{ flex: 1 }} />
                  <Button onClick={() => { setAddHistory(false); setError(''); }} disabled={loading}>Cancel</Button>
                  <Button
                    variant="contained"
                    sx={getQuestriPrimaryButtonSx()}
                    onClick={handleAddHistoricalRecords}
                    loading={loading}
                    disabled={loading || !!validationError}
                  >Save completions
                  </Button>
                </Stack>
              </Box>
            )}
            <Divider />
            {nextEvent && (
              <Box component="section" aria-labelledby={upcomingTitleId}>
                <Typography id={upcomingTitleId} variant="subtitle1" component="h3">Upcoming</Typography>
                <UpcomingRow
                  {...upcomingRowProps}
                  sx={{
                    px: 1,
                    py: 0.75,
                    gap: 1,
                    borderRadius: 1,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}><EventOutlined /></ListItemIcon>
                  <ListItemText
                    primary={`Scheduled for ${moment(nextDate).format('LL')}`}
                    secondary={moment(nextDate).fromNow()}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Chip size="small" variant="outlined" label="Scheduled" />
                  {!readOnly && <ChevronRight fontSize="small" sx={{ color: 'text.secondary' }} />}
                </UpcomingRow>
              </Box>
            )}
            <Box component="section" aria-labelledby={historyTitleId}>
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography id={historyTitleId} variant="subtitle1" component="h3">History</Typography>
                {!readOnly && !addHistory && (
                  <Button size="small" startIcon={<Add />} onClick={openHistory} disabled={loading || hideModal}>Add past completion</Button>
                )}
              </Stack>
              <TaskHistory
                reminderId={tagReminder.id}
                knownTask={lastEvent}
                refreshKey={historyVersion}
                eventTemplate={template}
                tag={tagReminder.tag.id}
                inline
                compact
                readOnly={readOnly}
                disabled={loading || hideModal}
                handleTaskSelect={handleTaskSelect}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

TagReminderModal.propTypes = {
  tagReminder: PropTypes.object.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  zIndex: PropTypes.number,
  triggerDataRefresh: PropTypes.func,
  readOnly: PropTypes.bool,
  onReminderRefresh: PropTypes.func,
  onReminderUnavailable: PropTypes.func,
  onEditorRefresh: PropTypes.func,
};

TagReminderModal.defaultProps = {
  open: false,
  onClose: () => { },
  zIndex: 1100,
  triggerDataRefresh: () => {},
  readOnly: false,
  onReminderRefresh: () => {},
  onReminderUnavailable: () => {},
  onEditorRefresh: () => {},
};

export default TagReminderModal;
